import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAcademy } from '../../context/AcademyContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { formatDateRange } from '../../utils/campLogic';

export default function Departments() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    departments,
    sessions,
    accessibleRuns,
    currentRun,
    selectRun,
    getRunSlug,
    isEnabled,
    loadingAcademy
  } = useAcademy();

  const enterRun = (run) => {
    const selected = selectRun(run);

    if (selected) {
      const runPath = `/hub/${getRunSlug(run)}`;
      const requestedPath = location.state?.from;
      const destination = requestedPath?.startsWith(runPath)
        ? requestedPath
        : runPath;

      navigate(destination, { replace: true });
    }
  };

  if (loadingAcademy) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <SafeIcon name="Loader" className="animate-spin text-4xl text-amber" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream px-5 py-12">
      <div className="mx-auto max-w-6xl space-y-8">
        <header className="text-center">
          <SafeIcon
            name="BookOpen"
            className="mx-auto mb-4 text-5xl text-amber"
          />
          <h1 className="text-4xl font-bold text-dark-green">
            Choose Your Learning Session
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-muted">
            Enter an active week-long event or return to content from a
            previous session.
          </p>
        </header>

        {departments.filter(isEnabled).map((department) => {
          const centerSessions = sessions.filter(
            (session) =>
              isEnabled(session) &&
              String(session?.department_id) === String(department?.id)
          );

          return (
            <section key={department.id} className="space-y-4">
              <div>
                <h2 className="text-2xl font-bold text-dark-green">
                  {department.name}
                </h2>
                {department.description && (
                  <p className="mt-1 text-muted">{department.description}</p>
                )}
              </div>

              {centerSessions.map((session) => {
                const sessionRuns = accessibleRuns.filter(
                  (run) =>
                    isEnabled(run) &&
                    String(run?.session_id) === String(session?.id)
                );

                if (sessionRuns.length === 0) return null;

                return (
                  <div key={session.id} className="space-y-4">
                    {session.featured_image && (
                      <div className="w-full h-48 md:h-64 rounded-xl overflow-hidden shadow-sm border border-tan">
                        <img
                          src={session.featured_image}
                          alt={session.title || session.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}

                    <div className="grid gap-5 md:grid-cols-2">
                      {sessionRuns.map((run) => (
                        <Card
                          key={run.id}
                          className={`border-2 ${
                            String(currentRun?.id) === String(run.id)
                              ? 'border-forest-green'
                              : 'border-tan'
                          }`}
                        >
                          <div className="mb-5">
                            <p className="text-xs font-bold uppercase tracking-widest text-amber">
                              Session: {session.title || session.name}
                            </p>
                            <h3 className="mt-2 text-xl font-bold text-dark-green">
                              {run.title || run.name}
                            </h3>
                            <p className="mt-2 text-sm text-muted">
                              {formatDateRange(run.start_date, run.end_date)}
                            </p>
                            <p className="mt-2 text-xs text-muted">
                              {department.name} · {session.title || session.name}
                            </p>
                          </div>

                          <Button
                            className="w-full"
                            onClick={() => enterRun(run)}
                          >
                            <SafeIcon name="ArrowRight" />
                            {String(currentRun?.id) === String(run.id)
                              ? 'Continue Session'
                              : 'Enter Session'}
                          </Button>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })}

        {!accessibleRuns.some(isEnabled) && (
          <Card className="py-12 text-center">
            <SafeIcon
              name="Calendar"
              className="mx-auto mb-3 text-4xl text-amber"
            />
            <h2 className="text-xl font-bold text-dark-green">
              No sessions are open
            </h2>
            <p className="mt-2 text-muted">
              Check back when the next learning center session launches.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}